// src/components/polling/Polling.js
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import Navbar from '../navbar/Navbar.js';
import AdBanner from '../ad_banner/AdBanner.js';
import { ToastContainer } from 'react-toastify';
import { showToast } from '../Notify.js';
import './polling.css';
import { api } from '../../api/index';

const Polling = () => {
  const [pollingData, setPollingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  const navigate = useNavigate();
  const { adBanners, adsEnabled } = useContext(AppContext);

  // Get deck_id from localStorage (set by Home.js when QR code is scanned)
  const deckId = localStorage.getItem('deck_id_for_polling');
  const deviceId = localStorage.getItem('device_id');
  const storeId = localStorage.getItem('store_id');

  // Fetch polling configuration on mount
  useEffect(() => {
    const fetchPollingConfig = async () => {
      console.log('Polling component mounted. deckId:', deckId);

      if (!deckId) {
        setError('Invalid deck ID. Please scan a valid QR code.');
        setLoading(false);
        return;
      }

      try {
        const backendUrl = api;
        const apiUrl = `${backendUrl}/polls/${deckId}`;
        console.log('Fetching polling data from:', apiUrl);
        console.log('Backend URL:', backendUrl);
        console.log('Device ID:', deviceId);
        console.log('Store ID:', storeId);

        const response = await fetch(apiUrl, {
          signal: AbortSignal.timeout(10000)
        });

        console.log('Polling API response status:', response.status);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch polling configuration`);
        }

        const result = await response.json();
        console.log('Polling API response:', result);

        if (result.data) {
          // Check poll status before showing polling UI
          if (result.data.poll_status === 'not_started') {
            setError({
              type: 'not_started',
              startDate: result.data.start_date,
              message: 'This polling event hasn\'t started yet.'
            });
            return;
          }
          if (result.data.poll_status === 'expired') {
            setError({
              type: 'expired',
              endDate: result.data.end_date,
              message: 'This polling event has expired.'
            });
            return;
          }
          setPollingData(result.data);
          setError(null);
        } else {
          setError({ type: 'general', message: 'No polling configuration found for this deck' });
        }
      } catch (err) {
        console.error('Error fetching polling data:', err);
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);
        console.error('Full error:', err);

        // Provide user-friendly error messages
        let errorMessage = 'Failed to load polling configuration';
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          errorMessage = 'Cannot connect to the server. Please check your internet connection and try again.';
        } else {
          errorMessage = err.message || 'Failed to load polling configuration';
        }
        setError({ type: 'general', message: errorMessage });
      } finally {
        setLoading(false);
      }
    };

    fetchPollingConfig();
  }, [deckId]);

  // Note: Success screen now stays visible indefinitely (no auto-redirect)

  // Generate a unique reward code
  const generateRewardCode = () => {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  };

  // Handle option selection and immediately submit
  const handleSelectOption = async (option) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSelectedOption(option);

    try {
      const backendUrl = api;
      console.log('Submitting poll response for option:', option);

      const response = await fetch(`${backendUrl}/polls/poll-responses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          poll_id: pollingData.poll_id,
          option_id: option.option_id,
          device_id: deviceId || null,
          ip_address: null,
          user_agent: navigator.userAgent,
          session_id: null
        }),
        signal: AbortSignal.timeout(10000)
      });

      console.log('Poll response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to submit poll response');
      }

      const result = await response.json();
      console.log('Poll response submitted successfully:', result);

      showToast('Thank you for voting!', 'success');

      // Generate reward code and navigate to outcome screen
      const rewardCode = generateRewardCode();
      setSuccessData({ rewardCode });

      // Navigate to outcome screen after a short delay
      setTimeout(() => {
        navigate(`/polling-outcome/${pollingData.poll_id}/${rewardCode}/${option.option_id}`);
      }, 800);
    } catch (err) {
      console.error('Error submitting poll response:', err);
      showToast(err.message || 'Failed to submit response', 'error');
      setIsSubmitting(false);
      setSelectedOption(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="polling-container">
          <div className="polling-loading">
            <p>Loading polling configuration...</p>
          </div>
        </div>
      </>
    );
  }

  // Format date helper
  const formatDate = (isoString) =>
    new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  // Error state
  if (error) {
    const isDateError = error.type === 'not_started' || error.type === 'expired';
    return (
      <>
        <Navbar />
        <div className="polling-container">
          <div className="polling-error">
            <div className="polling-date-error-icon">
              {error.type === 'expired' ? '⏰' : '🗓️'}
            </div>
            <h2>{error.message}</h2>
            {error.type === 'not_started' && error.startDate && (
              <p>This event opens on <strong>{formatDate(error.startDate)}</strong></p>
            )}
            {error.type === 'expired' && error.endDate && (
              <p>This event closed on <strong>{formatDate(error.endDate)}</strong></p>
            )}
            {!isDateError && (
              <button
                className="polling-back-btn"
                onClick={() => navigate('/')}
              >
                Go Back
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // Note: Success state will automatically navigate to outcome screen, so we don't show a screen here

  // No polling data
  if (!pollingData) {
    return (
      <>
        <Navbar />
        <div className="polling-container">
          <div className="polling-error">
            <p>No polling configuration available</p>
            <button
              className="polling-back-btn"
              onClick={() => navigate('/')}
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  // Parse text styles
  const textStyle = typeof pollingData.text_style === 'string'
    ? JSON.parse(pollingData.text_style)
    : pollingData.text_style || {};

  const backgroundColor = pollingData.background_color || '#fafafa';
  const pollTitle = pollingData.title || 'What do you prefer?';
  const pollOptions = pollingData.options || [];

  return (
    <>
      <Navbar />
      <div
        className="polling-container"
        style={{ backgroundColor }}
      >
        {adsEnabled && adBanners.length > 0 && <AdBanner banners={adBanners} />}

        <div className="polling-content">
          {/* Question */}
          <div className="polling-question">
            <h1
              style={{
                fontFamily: textStyle.font_family || 'Arial, sans-serif',
                fontSize: textStyle.font_size || '48px',
                fontWeight: textStyle.font_weight || 'normal',
                fontStyle: textStyle.font_style || 'normal',
                color: textStyle.color || '#000000',
                margin: '20px 0',
                textAlign: 'center'
              }}
            >
              {pollTitle}
            </h1>
          </div>

          {/* Options Grid */}
          <div className="polling-options">
            {pollOptions.map((option) => (
              <button
                key={option.option_id}
                className={`polling-option ${selectedOption?.option_id === option.option_id ? 'selected' : ''}`}
                onClick={() => handleSelectOption(option)}
                disabled={isSubmitting}
              >
                <div className="polling-option-icon">
                  <img
                    src={option.icon_url}
                    alt={option.label}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <div className="polling-option-label" style={{ color: textStyle.color}}>
                  {option.label}
                </div>
              </button>
            ))}
          </div>

        </div>

        <ToastContainer />
      </div>
    </>
  );
};

export default Polling;
